import { smBumps, useModal, xlBumps, xxlBumps } from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import { getCurrentQuote } from '../../../../../domain'
import { useApiErrorHandlingAsyncCallback } from '../../../../../hooks/auth'
import { useCheckoutBind } from '../../../../../hooks/checkout'
import { useEditProposalCallback } from '../../../../../hooks/proposal'
import {
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { usePrincipal } from '../../../../authentication'
import { YourCoverSection } from '../../../../organisms/covers'
import { ProposalDocumentsSection } from '../../../../organisms/product'
import {
  ProposalAutoRenewalSection,
  ProposalInformationSection,
} from '../../../../organisms/proposal'
import { FullPage } from '../../../../templates'
import { usePaymentSchedule } from '../../../paymentScheduleContext'
import { useProduct } from '../../../productContext'
import { ProposalSummaryPricing } from '../../pricing'
import { useProposal } from '../../proposalContext'
import { OutstandingBalanceModal } from './components/OutstandingBalanceModal'
import { QuoteInformationSection } from './components/QuoteInformationSection'
import { StartCheckoutSection } from './components/StartCheckoutSection'
import { VerificationFailedModal } from './components/VerificationFailedModal'

const tKey = TKey('pages.proposal-summary')

export const ProposalSummaryPage: FunctionComponent = () => {
  const { t } = useWriskTranslation()
  const { apiClient } = usePrincipal()

  const { isPaymentScheduleDirty, getScheduleChanges } = usePaymentSchedule()

  const modal = useModal()

  const { product } = useProduct()
  const { proposal, setProposal } = useProposal()

  const quote = getCurrentQuote(proposal)

  const onEditDisclosures = useEditProposalCallback(proposal)

  const { execute: updateProposal } = useApiErrorHandlingAsyncCallback(async () => {
    if (isPaymentScheduleDirty) {
      setProposal(
        await apiClient.updateProposal(proposal.proposalCode, {
          changes: getScheduleChanges(),
        }),
      )
    }
  })

  const onCheckoutBind = useCheckoutBind(
    (checks) => {
      modal.show({
        content: <VerificationFailedModal checks={checks} />,
      })
    },
    () => modal.show({ content: <OutstandingBalanceModal /> }),
  )

  const onConfirm = async () => {
    await updateProposal()
    return onCheckoutBind.execute()
  }

  const autoRenewalSectionSubheader = t(tKey('sections.auto-renewal.subheader'), {
    defaultValue: null,
  })

  return (
    <FullPage pageId='proposal-summary-page' header={t(tKey('header'))}>
      <ProposalSummaryPricing
        product={product}
        proposal={proposal}
        quote={quote}
        mb={smBumps}
      />
      <ProposalInformationSection proposal={proposal} mb={xxlBumps} />
      <ProposalAutoRenewalSection
        proposal={proposal}
        mb={xxlBumps}
        subheader={autoRenewalSectionSubheader}
      />
      <QuoteInformationSection onEditDetails={onEditDisclosures} quote={quote} />
      <YourCoverSection quote={quote} />
      <ProposalDocumentsSection quote={quote} mb={xlBumps} />
      <StartCheckoutSection
        product={product}
        onConfirm={onConfirm}
        loading={onCheckoutBind.loading}
      />
    </FullPage>
  )
}
